import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@astro/auth'
import { api } from '@astro/api'

/** Unwrap `{ status, data }` envelopes and paginators into a plain line array. */
function toLines(res) {
  const d = res && typeof res === 'object' && 'data' in res ? res.data : res
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.data)) return d.data
  return []
}

/** Upstream signals business failures as HTTP 200 + { status: false } —
 * rethrow those so they count as rejected, not as a successful push. */
export function ensureOk(res) {
  if (res && typeof res === 'object' && res.status === false) {
    throw new Error(res.message || 'Request rejected by server')
  }
  return res
}

/**
 * Server-truth wishlist membership. The heart icon on a product card must
 * only ever flip once the API confirms the change — not the moment the user
 * clicks — so this exposes no local optimistic state: `has(productId)` is
 * derived straight from the last successful `['wishlist']` fetch, and
 * `toggle` just fires the mutation + invalidation that drives the refetch.
 */
export function useWishlist() {
  const { status } = useAuth()
  const authed = status === 'authenticated'
  const queryClient = useQueryClient()

  const wishlistQuery = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => api.wishlist.list(),
    enabled: authed,
    retry: false,
    // Every heart icon on the page (product cards, similar-items rails,
    // header badge) mounts its own observer on this same query key. Without
    // a staleTime, React Query's default refetch-on-mount fires a fresh
    // /wishlist call each time a new batch of cards mounts (e.g. navigating
    // between product pages) even though membership rarely changes.
    // `toggle` below invalidates this key immediately on any real change,
    // so a stale cache never shows a wrong heart state.
    staleTime: 5 * 60 * 1000,
  })

  const lines = useMemo(() => toLines(wishlistQuery.data), [wishlistQuery.data])

  // productId → wishlist row id: upstream deletes by the row, not the product.
  const rowIds = useMemo(
    () => new Map(lines.map((l) => [String(l.product_id ?? l.product?.id), l.id])),
    [lines]
  )

  const has = (productId) => rowIds.has(String(productId))

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['wishlist'] })

  /** Delete one wishlist line by its row id; rejects on the server's verdict. */
  const removeLine = async (rowId) => {
    const res = ensureOk(await api.wishlist.remove(rowId))
    invalidate()
    return res
  }

  /** Fires the API call and refetches — resolves/rejects on the server's
   * actual verdict so callers can tell the user what really happened. */
  const toggle = async (productId, currentlyWishlisted) => {
    if (currentlyWishlisted) return removeLine(rowIds.get(String(productId)))
    const res = ensureOk(await api.wishlist.add(productId))
    invalidate()
    return res
  }

  return {
    lines,
    has,
    toggle,
    removeLine,
    count: lines.length,
    authed,
    isPending: authed && wishlistQuery.isPending,
    isLoading: authed && wishlistQuery.isFetching,
  }
}
